import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Heart, Shield, Globe, MessageCircle, Star, Users, ArrowRight, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    { icon: Heart, title: 'Smart Matching', description: 'AI-powered compatibility based on cultural values' },
    { icon: Shield, title: 'Verified Profiles', description: 'Identity verification for safe connections' },
    { icon: Globe, title: 'Translation', description: 'Break language barriers with instant translation' },
    { icon: MessageCircle, title: 'Real-time Chat', description: 'Connect instantly with video calls & messaging' },
  ];

  const stats = [
    { value: '50K+', label: 'Active Members' },
    { value: '1000+', label: 'Success Stories' },
    { value: '54', label: 'African Countries' },
  ];

  return (
    <div className="min-h-screen bg-dark" data-testid="landing-page">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1745572587190-962fe038f073?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-dark/50 via-dark/70 to-dark" />

        {/* Navigation */}
        <nav className="relative z-10 flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <Heart className="w-8 h-8 text-gold" fill="#D4AF37" />
            <span className="font-heading text-xl text-white">African Connections</span>
          </div>
          <Button
            onClick={() => navigate('/login')}
            variant="ghost"
            className="text-white hover:text-gold font-body"
            data-testid="login-btn"
          >
            Sign In
          </Button>
        </nav>

        {/* Hero content */}
        <div className="relative z-10 px-6 pt-16 pb-24 text-center max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
              Find Love Across{' '}
              <span className="text-gold-gradient">Continents</span>
            </h1>
            <p className="text-lg text-gray-300 font-body mb-8 max-w-md mx-auto">
              The premier dating platform connecting Western men with African women worldwide
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/register')}
                className="btn-gold h-14 px-8 rounded-full text-lg font-body font-semibold"
                data-testid="get-started-btn"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="h-14 px-8 rounded-full text-lg font-body border-white/20 text-white hover:bg-white/5"
              >
                I have an account
              </Button>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="py-12 px-6 border-y border-white/10">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="text-center"
            >
              <p className="font-heading text-3xl sm:text-4xl text-gold-gradient">{stat.value}</p>
              <p className="text-gray-400 font-body text-sm mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl text-center text-white mb-12">
            Why Choose <span className="text-gold">African Connections</span>
          </h2>

          <div className="grid sm:grid-cols-2 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 bg-dark-paper border border-white/5 rounded-2xl card-hover"
                >
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="font-heading text-xl text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 font-body">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Success Stories Preview */}
      <section className="py-16 px-6 bg-dark-paper">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-white mb-4">
            Real <span className="text-gold">Love Stories</span>
          </h2>
          <p className="text-gray-400 font-body mb-8">
            Thousands of couples have found their soulmate on African Connections
          </p>

          <div className="flex justify-center gap-4 mb-8">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gold">
              <img
                src="https://images.pexels.com/photos/15367697/pexels-photo-15367697.jpeg"
                alt="Success couple"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gold -ml-4">
              <img
                src="https://images.pexels.com/photos/15522671/pexels-photo-15522671.jpeg"
                alt="Success couple"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <blockquote className="text-lg text-gray-300 font-body italic max-w-lg mx-auto mb-4">
            "We met on African Connections and now we're happily married! The cultural filters helped us find each other."
          </blockquote>
          <p className="text-gold font-body mb-6">— John & Amara, USA & Nigeria</p>
          
          <button
            onClick={() => navigate('/success-stories')}
            className="text-gold hover:underline font-body"
            data-testid="view-stories-btn"
          >
            View More Success Stories →
          </button>
        </div>
      </section>

      {/* Premium CTA */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Crown className="w-16 h-16 text-gold mx-auto mb-4" />
          <h2 className="font-heading text-3xl text-white mb-4">
            Choose Your <span className="text-gold-gradient">Plan</span>
          </h2>
          <p className="text-gray-400 font-body mb-8">
            Find your perfect match with our flexible membership options
          </p>
          
          {/* Membership Tiers */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {/* Free Tier */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 }}
              className="p-6 bg-dark-paper border border-white/10 rounded-2xl"
            >
              <h3 className="font-heading text-xl text-white mb-2">Free</h3>
              <p className="text-3xl text-white font-heading mb-4">$0</p>
              <ul className="text-sm text-gray-400 space-y-2 mb-6 text-left">
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gold" />
                  30 swipes per day
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gold" />
                  Basic matching
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gold" />
                  Messaging
                </li>
              </ul>
              <Button
                onClick={() => navigate('/register')}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/5"
              >
                Get Started
              </Button>
            </motion.div>

            {/* Gold Tier - Most Popular */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-6 bg-gradient-to-b from-gold/20 to-dark-paper border border-gold rounded-2xl relative"
            >
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gold text-black text-xs font-bold rounded-full">
                MOST POPULAR
              </span>
              <h3 className="font-heading text-xl text-gold mb-2">Gold</h3>
              <p className="text-3xl text-white font-heading mb-1">$14.99</p>
              <p className="text-gray-400 text-sm mb-4">/month</p>
              <ul className="text-sm text-gray-300 space-y-2 mb-6 text-left">
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gold" />
                  Unlimited swipes
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gold" />
                  See who likes you
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gold" />
                  1 Boost per month
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gold" />
                  Read receipts
                </li>
              </ul>
              <Button
                onClick={() => navigate('/register')}
                className="w-full btn-gold"
              >
                Start Free Trial
              </Button>
            </motion.div>

            {/* Platinum Tier */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-6 bg-dark-paper border border-white/10 rounded-2xl"
            >
              <h3 className="font-heading text-xl text-white mb-2">Platinum</h3>
              <p className="text-3xl text-white font-heading mb-1">$29.99</p>
              <p className="text-gray-400 text-sm mb-4">/month</p>
              <ul className="text-sm text-gray-400 space-y-2 mb-6 text-left">
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gold" />
                  Everything in Gold
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gold" />
                  5 Super Likes/day
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gold" />
                  Weekly Boost
                </li>
                <li className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-gold" />
                  Priority support
                </li>
              </ul>
              <Button
                onClick={() => navigate('/register')}
                variant="outline"
                className="w-full border-gold/50 text-gold hover:bg-gold/10"
              >
                Start Free Trial
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/10">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-gold" fill="#D4AF37" />
            <span className="font-body text-gray-400">African Connections</span>
          </div>
          <div className="flex gap-6 text-sm text-gray-400 font-body">
            <button onClick={() => navigate('/safety')} className="hover:text-white transition-colors">Safety</button>
            <button onClick={() => navigate('/success-stories')} className="hover:text-white transition-colors">Stories</button>
            <button onClick={() => navigate('/country-guides')} className="hover:text-white transition-colors">Guides</button>
            <button onClick={() => navigate('/help')} className="hover:text-white transition-colors">Help</button>
          </div>
          <p className="text-gray-500 text-sm font-body">© 2025 African Connections</p>
        </div>
      </footer>
    </div>
  );
}
